import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/external-client";
import { activeTripStore } from "@/lib/activeTripStore";

/**
 * App-wide watcher that keeps `activeTripStore` honest when a trip is
 * completed/cancelled externally (e.g. by the Admin Portal) while the
 * driver is on a screen other than DriverHome (Profile, Trips, etc.).
 *
 * Without this, the Sign Out button in DriverProfile would remain
 * disabled until the user kills + reopens the app, because the only
 * Realtime listener on `bookings` lives inside DriverHome.
 *
 * - Subscribes to UPDATEs on the active booking row.
 * - On terminal status (anything other than `in_progress`), clears the
 *   store so subscribers (Sign Out button, etc.) re-enable instantly.
 * - Reconciles via a one-shot fetch on mount, `visibilitychange`, and
 *   `online` to catch any missed Realtime frames.
 */
export function useActiveTripWatcher(driverId: string | null | undefined) {
  // We track the booking id we are currently subscribed to so we can
  // re-subscribe when it rotates without re-running effect dependencies
  // on every store change.
  const subscribedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!driverId) return;

    let cancelled = false;
    let currentChannel: ReturnType<typeof supabase.channel> | null = null;

    const reconcile = async (bookingId: string) => {
      try {
        const { data, error } = await supabase
          .from("bookings" as any)
          .select("status")
          .eq("id", bookingId)
          .maybeSingle();
        if (cancelled || error) return;
        const status = (data as any)?.status;
        if (!data || (status && status !== "in_progress")) {
          activeTripStore.clear();
        }
      } catch {
        /* ignore — Realtime will catch up */
      }
    };

    const teardown = () => {
      if (currentChannel) {
        try { supabase.removeChannel(currentChannel); } catch { /* ignore */ }
        currentChannel = null;
      }
      subscribedIdRef.current = null;
    };

    const ensureSubscription = () => {
      const entry = activeTripStore.get();
      const bookingId = entry?.bookingId ?? null;

      if (bookingId === subscribedIdRef.current) return;
      teardown();
      if (!bookingId) return;

      subscribedIdRef.current = bookingId;

      currentChannel = supabase
        .channel(`active-trip-watch-${bookingId}`)
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "bookings",
            filter: `id=eq.${bookingId}`,
          },
          (payload) => {
            const status = (payload.new as any)?.status;
            if (status && status !== "in_progress") {
              activeTripStore.clear();
            }
          },
        )
        .subscribe();

      // Safety-net: also reconcile via REST in case we missed the event
      // between unmount and resubscribe.
      reconcile(bookingId);
    };

    // React to store changes (start/clear/replace booking).
    const unsubStore = activeTripStore.subscribe(ensureSubscription);
    ensureSubscription();

    // Reconcile on resume / network recovery in case Realtime dropped a frame.
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      const entry = activeTripStore.get();
      if (entry?.bookingId) reconcile(entry.bookingId);
    };
    const onOnline = () => {
      const entry = activeTripStore.get();
      if (entry?.bookingId) reconcile(entry.bookingId);
    };
    window.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onOnline);

    return () => {
      cancelled = true;
      unsubStore();
      window.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onOnline);
      teardown();
    };
  }, [driverId]);
}